import { useMemo } from 'react';
import {
  useGetActiveManufacturersQuery,
  useGetManufacturerByIdQuery,
} from './manufacturers-api-slice';
import { useAppSelector } from '../../store/hooks';
import { selectManageManufacturers } from '../user';

/**
 * Custom hook to fetch manufacturer data based on user role and context
 *
 * This hook centralizes the logic for fetching manufacturer data in the user profile page,
 * handling the different data access patterns for admin vs non-admin users.
 */
export const useManufacturerData = (options: {
  isEditMode: boolean;
  profileManufacturerId?: number;
  currentUserManufacturerId?: number;
}) => {
  const { isEditMode, profileManufacturerId, currentUserManufacturerId } = options;

  const canManageManufacturers = useAppSelector(selectManageManufacturers);

  // For admin users, fetch all manufacturers
  const { data: allManufacturers = [], isLoading: isLoadingAllManufacturers } =
    useGetActiveManufacturersQuery(undefined, {
      skip: !canManageManufacturers, // Skip this query for non-admin users
    });

  // Determine which manufacturer ID to fetch for non-admin users
  const manufacturerIdToFetch = useMemo(() => {
    if (canManageManufacturers) {
      return undefined;
    } else if (isEditMode && profileManufacturerId) {
      return profileManufacturerId;
    } else {
      return currentUserManufacturerId;
    }
  }, [isEditMode, profileManufacturerId, currentUserManufacturerId, canManageManufacturers]);

  // Only fetch single manufacturer for non-admin users when needed
  const { data: singleManufacturer, isLoading: isLoadingSingleManufacturer } =
    useGetManufacturerByIdQuery(manufacturerIdToFetch?.toString() ?? '', {
      skip: canManageManufacturers || !manufacturerIdToFetch,
      refetchOnMountOrArgChange: true,
    });

  // Combine the results
  const manufacturers = useMemo(() => {
    if (canManageManufacturers) {
      return allManufacturers;
    } else if (singleManufacturer) {
      return [singleManufacturer];
    }
    return [];
  }, [canManageManufacturers, allManufacturers, singleManufacturer]);

  const isLoading = canManageManufacturers
    ? isLoadingAllManufacturers
    : isLoadingSingleManufacturer;

  return { manufacturers, isLoading, manufacturerIdToFetch };
};
